import { Injectable } from "@angular/core";
import { PlatformService } from "services/platform.service";

// Declare CueWidget on window object
declare global {
	interface Window {
		CueWidget?: {
			isReady: boolean;
			updateInitialSessionData: (data: Record<string, any>) => void;
			open?: () => void;
			show?: () => void;
			toggle?: () => void;
		};
		cueWidgetConfig?: {
			id: string;
			onReady?: () => void;
		};
		cueWidgetReadyCallbacks?: Array<() => void>;
	}
}

@Injectable({
	providedIn: "root"
})
export class CueWidgetService {

	private pendingData: Record<string, any> | null = null;

	constructor(private platform: PlatformService) {
		// Setup listener for when widget becomes ready
		this.setupWidgetReadyListener();
	}

	private setupWidgetReadyListener(): void {
		if (this.platform.isServer) {
			return;
		}

		// Register callback to be called when widget is ready
		if (typeof window !== "undefined") {
			if (!window.cueWidgetReadyCallbacks) {
				window.cueWidgetReadyCallbacks = [];
			}
			window.cueWidgetReadyCallbacks.push(() => {
				// Send pending data if any
				if (this.pendingData) {
					console.log("CueWidget: Widget now ready, sending queued data");
					this.updateSessionData(this.pendingData);
					this.pendingData = null;
				}
			});
		}
	}

	/**
	 * Updates the initial session data for the Cue widget
	 * @param data - Key-value pairs of custom data to send to the widget
	 * @returns boolean - true if data was updated successfully, false otherwise
	 */
	updateSessionData(data: Record<string, any>): boolean {
		// Only run in browser environment
		if (this.platform.isServer) {
			return false;
		}

		// Check if CueWidget is available and ready
		if (typeof window !== "undefined" && window.CueWidget && window.CueWidget.isReady) {
			try {
				window.CueWidget.updateInitialSessionData(data);
				console.log("CueWidget: Session data updated", data);
				return true;
			} catch (error) {
				console.error("CueWidget: Error updating session data", error);
				return false;
			}
		} else {
			// Widget not ready yet - store data to send when ready
			this.pendingData = { ...this.pendingData, ...data };
			console.log("CueWidget: Widget not ready yet. Data queued to send once widget loads:", data);
			return false;
		}
	}

	/**
	 * Sets custom data for a user reservation
	 * @param reservationToken - Reservation token
	 * @param unitId - Unit ID
	 * @param locationName - Location name
	 */
	setReservationData(reservationToken: string, unitId: number, locationName: string): boolean {
		return this.updateSessionData({
			reservationToken: reservationToken,
			unitId: unitId.toString(),
			location: locationName,
			source: "website"
		});
	}

	/**
	 * Sets custom data for a user viewing a specific location
	 * @param locationName - Name of the location being viewed
	 * @param locationId - ID of the location
	 */
	setLocationData(locationName: string, locationId: number): boolean {
		return this.updateSessionData({
			viewingLocation: locationName,
			locationId: locationId.toString(),
			source: "website"
		});
	}

	/**
	 * Sets custom data for a user viewing units
	 * @param locationName - Name of the location
	 * @param unitSize - Size category of units being viewed
	 */
	setUnitBrowsingData(locationName: string, unitSize?: string): boolean {
		const data: Record<string, any> = {
			browsingLocation: locationName,
			source: "website"
		};

		if (unitSize) {
			data.unitSize = unitSize;
		}

		return this.updateSessionData(data);
	}

	/**
	 * Sets custom data for a user inquiry
	 * @param inquiryType - Type of inquiry (contact, vip, refer, etc.)
	 */
	setInquiryData(inquiryType: string): boolean {
		return this.updateSessionData({
			inquiryType: inquiryType,
			source: "website"
		});
	}

	/**
	 * Check if the CueWidget is ready
	 * @returns boolean - true if widget is ready, false otherwise
	 */
	isWidgetReady(): boolean {
		if (this.platform.isServer) {
			return false;
		}
		return !!(typeof window !== "undefined" && window.CueWidget && window.CueWidget.isReady);
	}

	/**
	 * Opens the WhatsApp widget popup
	 * @returns boolean - true if widget was opened successfully, false otherwise
	 */
	openWidget(): boolean {
		if (this.platform.isServer) {
			return false;
		}

		if (typeof window !== "undefined") {
			try {
				// Try to find clickable button inside the cue container
				const cueContainer = document.querySelector('#cue-app-container');
				if (cueContainer) {
					console.log("CueWidget: Found cue-app-container", cueContainer);

					// Look for button inside the container
					const buttonSelectors = [
						'button',
						'[role="button"]',
						'a',
						'div[onclick]',
						'.button',
						'[class*="button"]',
						'[class*="trigger"]',
						'[class*="launcher"]'
					];

					for (const btnSelector of buttonSelectors) {
						const button = cueContainer.querySelector(btnSelector);
						if (button && button instanceof HTMLElement) {
							console.log(`CueWidget: Found clickable element with selector: ${btnSelector}`, button);

							// Try multiple click methods
							button.click();

							// Also dispatch mouse events
							const mouseDownEvent = new MouseEvent('mousedown', { bubbles: true, cancelable: true, view: window });
							const mouseUpEvent = new MouseEvent('mouseup', { bubbles: true, cancelable: true, view: window });
							const clickEvent = new MouseEvent('click', { bubbles: true, cancelable: true, view: window });

							button.dispatchEvent(mouseDownEvent);
							button.dispatchEvent(mouseUpEvent);
							button.dispatchEvent(clickEvent);

							console.log("CueWidget: Dispatched click events");
							return true;
						}
					}

					// If no button found, try clicking the container itself
					console.log("CueWidget: No button found, clicking container");
					(cueContainer as HTMLElement).click();
				}

				// Try CueWidget API methods
				if (window.CueWidget) {
					console.log("CueWidget object found:", window.CueWidget);
					if (typeof (window.CueWidget as any).open === 'function') {
						console.log("CueWidget: Calling open()");
						(window.CueWidget as any).open();
						return true;
					} else if (typeof (window.CueWidget as any).show === 'function') {
						console.log("CueWidget: Calling show()");
						(window.CueWidget as any).show();
						return true;
					} else if (typeof (window.CueWidget as any).toggle === 'function') {
						console.log("CueWidget: Calling toggle()");
						(window.CueWidget as any).toggle();
						return true;
					} else if (typeof (window.CueWidget as any).openChat === 'function') {
						console.log("CueWidget: Calling openChat()");
						(window.CueWidget as any).openChat();
						return true;
					}

					// Log all available methods
					console.log("CueWidget available properties:", Object.keys(window.CueWidget));
				}

				console.warn("CueWidget: No method available to open widget");
				return false;
			} catch (error) {
				console.error("CueWidget: Error opening widget", error);
				return false;
			}
		}
		console.warn("CueWidget: Window not available");
		return false;
	}
}
